export enum JOURNEY_SCHEMA_VERSION {
  /**
   * Version 4
   * Introduced in {TODO: Add date}
   */
  V4 = 'v4'
}

export enum JOURNEY_TYPE {
  /**
   * Regular journey
   */
  REGULAR = 'regular',
  /** Journey that launches other journeys */
  LAUNCHER = 'launcher',
  /**
   * Post-qualification journey
   *
   * Currently, this is a regular journey with context parameters,
   * which are required to be filled with another journey.
   *
   * @note: This type is not implemented yet.
   */
  POST_QUALIFICATION = 'post-qualification'
}

export enum JOURNEY_ACCESS_MODE {
  PUBLIC = 'public',
  PRIVATE = 'private'
}

export enum JOURNEY_STATUS {
  PUBLISHED = 'published',
  DRAFT = 'draft'
}

export enum JOURNEY_EMBED_MODE {
  /**
   * Indicates the Journey is embedded in a full-screen,
   * although that might be either in an iframe (i.e. within a dialog) or not.
   */
  FULL_SCREEN = 'full-screen',
  /**
   * Indicates the Journey is embedded in a page, which certainly means
   * it's in an iframe.
   */
  INLINE = 'inline'
}

export enum JOURNEY_PURPOSE {
  /** Refers to {@link RUNTIME_ENTITY.ORDER} */
  SELL_360 = 'sell-360-products',
  /** Refers to both {@link RUNTIME_ENTITY.ORDER} and {@link RUNTIME_ENTITY.OPPORTUNITY} */
  REQUEST_360 = 'request-360-products',
  /** Refers to {@link RUNTIME_ENTITY.OPPORTUNITY} */
  GENERAL_REQUESTS = 'general-requests',
  /** Refers to 'none' or empty runtime entities */
  OTHER = 'other'
}

export enum RUNTIME_ENTITY {
  /** Maps to {@link JOURNEY_PURPOSE.SELL_360} */
  ORDER = 'ORDER',
  /** Maps to {@link JOURNEY_PURPOSE.GENERAL_REQUESTS} */
  OPPORTUNITY = 'OPPORTUNITY'
}

export enum BLOCK_TYPE {
  AUTH = 'auth',

  // Stateless
  GROUP = 'group',
  PARAGRAPH = 'paragraph',
  IMAGE = 'image',
  HYPERLINK = 'hyperlink',
  SUMMARY = 'summary',

  // Basic inputs
  TEXT_INPUT = 'text-input',
  NUMBER_INPUT = 'number-input',
  DATE_INPUT = 'date-input',
  BINARY_INPUT = 'binary-input',
  SINGLE_CHOICE = 'single-choice',
  MULTIPLE_CHOICE = 'multiple-choice',

  PERSONAL_INFORMATION = 'personal-information',
  CONTACT = 'contact',
  ACCOUNT = 'account',
  ADDRESS = 'address',
  SIGNATURE = 'signature',
  FILE_UPLOAD = 'file-upload',
  AVAILABILITY_CHECK = 'availability-check',

  PRODUCTS = 'products',
  PRODUCT_CATEGORIES = 'product-categories',
  PAYMENT_METHOD = 'payment-method',
  SHOPPING_CART = 'shopping-cart',
  INPUT_CALCULATOR = 'input-calculator',
  PRODUCT_RECOMMENDATIONS = 'product-recommendations',

  ACTION_BAR = 'action-bar',
  CONSENTS = 'consents',
  SUCCESS_MESSAGE = 'success-message',
  PDF_GENERATOR = 'pdf-generator',

  PREVIOUS_PROVIDER = 'previous-provider',
  PV_ROOFTOP = 'pv-rooftop',
  METER_READING = 'meter-reading',
  ENTITY_ATTRIBUTE = 'entity-attribute',
  ENTITY_FINDER = 'entity-finder',
  ATTRIBUTE_EDITOR = 'attribute-editor',

  // Third-party
  CUSTOM = 'custom',
  APP = 'app'
}

export enum REFERENCED_TYPE {
  /** Allows referencing a step */
  STEP = 'step',
  /** Allows referencing a block */
  BLOCK = 'block',
  /** Allows referencing a context parameter */
  CONTEXT_PARAMETER = 'context-parameter'
}

/**
 * Some values map back to Entities in our platform, therefore the "-entity" suffix.
 *
 * WARNING: Anything that isn't immediately supported as an "Entity" by the Journey App
 * is going to be considered a "text" type, even if it is an entity.
 */
export enum CONTEXT_PARAMETER_TYPE {
  TEXT = 'text',
  ACCOUNT = 'account-entity',
  CONTACT = 'contact-entity',
  CONTRACT = 'contract-entity',
  OPPORTUNITY = 'opportunity-entity',
  ORDER = 'order-entity',
  REQUEST = 'request',
  /** Also known as 'email' entity */
  MESSAGE = 'message-entity'
}

export enum LANGUAGE {
  DE = 'de',
  FR = 'fr',
  EN = 'en'
}

export enum LOGIC_ACTION {
  /**
   * Jumps to a specific Step
   *
   * Allows the user to specify to which Step they should jump to. Also allows the user
   * to control WHEN this should happen.
   */
  JUMP_TO_STEP = 'jump-to-step',
  /**
   * Skips a Step
   *
   * Allows skipping one or multiple Steps, regardless of their order.
   */
  SKIP_STEP = 'skip-step',
  /**
   * Determines whether a block should be displayed
   *
   * Allows the user to control the visibility of a given block, even if
   * that block was marked as required.
   *
   * If a required block is set to be hidden, the restriction is going to be lifted.
   */
  DISPLAY_CONDITION = 'display-condition',
  /**
   * Allows injecting data from one block to another.
   *
   * Depending on the case, it might also transform data and disable fields on the
   * destination block.
   */
  DATA_INJECTION = 'data-injection'
}

export enum LOGIC_OPERATOR {
  /** The fact is supposed to be strictly equal to the comparison value */
  EQUAL = 'equal',
  /** The fact is supposed to NOT be strictly equal to the comparison value */
  NOT_EQUAL = 'not-equal',
  /** The fact is supposed to be a number and greater than the comparison value */
  GREATER_THAN = 'greater-than',
  /** The fact is supposed to be a number and greater than or equal to the comparison value */
  GREATER_THAN_OR_EQUAL = 'greater-than-or-equal',
  /** The fact is supposed to be a number and lower than the comparison value */
  LESS_THAN = 'less-than',
  /** The fact is supposed to be a number and lower than or equal to the comparison value */
  LESS_THAN_OR_EQUAL = 'less-than-or-equal',
  /** The fact is supposed to be an array and contain at least one of the comparison values */
  CONTAIN_EITHER = 'contain-either',
  /**
   * The fact is expected to be a single value, while the comparison value is expected to be an array.
   * The fact needs to be contained in the list of comparison values.
   */
  IN = 'in',
  /**
   * The fact is expected to be a single value, while the comparison value is expected to be an array.
   * The fact must NOT be contained in the list of comparison values.
   */
  NOT_IN = 'not-in',
  /**
   * The fact is expected to be an array, while the comparison value a single value.
   * The fact must contain the comparison value.
   */
  CONTAIN = 'contain',
  /**
   * The fact is expected to be an array, while the comparison value a single value.
   * The fact must NOT contain the comparison value.
   */
  NOT_CONTAIN = 'not-contain',
  /** The fact is supposed to be a string and start with another (case-snsitive) */
  STARTS_WITH = 'starts-with',
  /** The fact is supposed to be a string and end with another (case-snsitive) */
  ENDS_WITH = 'ends-with',
  /** The fact is supposed to be a "nullish" value (null, undefined, or an empty array/string) */
  EMPTY = 'empty',
  /** The fact is NOT supposed to be a "nullish" value (null, undefined, or an empty array/string) */
  NOT_EMPTY = 'not-empty',
  /** The fact is supposed to be a date or a number and be between two comparison values */
  BETWEEN = 'between',
  /** The fact is supposed to match a regular expression */
  MATCH = 'match',
  /** The fact is supposed to NOT match a regular expression */
  NOT_MATCH = 'not-match',
  /**
   * Considered a special operator, the function operator has no specific implementation but rather
   * allows different parts of the application (i.e. Blocks) to express custom logic that can be used
   * to assert clauses.
   *
   * This is useful to perform custom logic that is not covered by the standard operators and does not directly
   * concern the logics engine. Currently, this is used by the AvailabilityCheck block to perform async API calls
   * that check the availability of a product.
   *
   * Idea: we might allow Custom Apps to define their own functions as well.
   */
  CUSTOM_FUNCTION = 'custom-function'
}

export enum LAYOUT_TYPE {
  STACK = 'stack',
  TWO_COLUMN = 'two-column',
  GRID = 'grid'
}

export enum JOURNEY_SAVING_PROGRESS_MODE {
  AUTO = 'auto',
  NONE = 'none',
  /** @deprecated not yet implemented */
  LOCAL = 'local',
  /** @deprecated not yet implemented */
  REMOTE = 'remote'
}

export enum COUNTRY_CODE {
  Germany = 'DE',
  Austria = 'AT',
  Switzerland = 'CH',
  Luxembourg = 'LU'
}

/**
 * This is directly related to `units.$unit` locales, whereas $unit
 * maps to this enum values.
 *
 * WARNING: If you change things, please ensure locales and references are updated
 * accordingly otherwise many UI compoennts might look broken.
 */
export enum UNITS {
  /**
   * Generic unit is simply a number.
   * This is confirmed to be used by number inputs, possibly other blocks as well.
   */
  GENERIC = 'generic',
  noUnit = 'noUnit',

  // These units can be used for variable prices
  m = 'm',
  m2 = 'm²',
  'cubic-meter' = 'm³',
  'cubic-meter-h' = 'm³/h',
  l = 'l',
  ls = 'l/s',
  a = 'A',
  kva = 'kVA',
  w = 'W',
  kw = 'kW',
  kwh = 'kWh',
  wp = 'Wp',
  kwp = 'kWp',

  // Other units
  rm = 'rm',
  t = 't',
  kg = 'kg',
  '°' = '°',
  hz = 'Hz',
  kvar = 'kVAr',
  kv = 'kV',
  '1/min' = '1/min',
  mh = 'mH',
  ka = 'kA',
  'kva/s' = 'kVA/s',

  // These units are from Meter Reading Units
  wh = 'Wh',
  kvarh = 'kvarh',
  mw = 'mW',
  mwh = 'mWh',
  unit = 'unit',
  hour = 'hour',
  day = 'day',
  month = 'month',
  year = 'year',
  percentage = 'percentage'
}

export enum VARIABLE_PRICE_UNITS {
  noUnit = 'noUnit',

  // Units that can be defined in a price or price component
  m = 'm',
  m2 = 'm²',
  'cubic-meter' = 'm³',
  'cubic-meter-h' = 'm³/h',
  l = 'l',
  ls = 'l/s',
  a = 'A',
  kva = 'kVA',
  w = 'W',
  kw = 'kW',
  kwh = 'kWh',
  wp = 'Wp',
  kwp = 'kWp'
}

export enum KNOWN_EVENT_SENDERS {
  BUILDER_APP = '@epilot360/journey-builder',
  DESIGN_BUILDER_APP = '@epilot360/design-builder',
  JOURNEY_APP = '@epilot/journey-app',
  ECP_PORTAL = '@epilot/ecp-portal'
}

export enum APP_EVENT {
  PREVIEW_LISTENER_READY = 'preview-listener-is-ready',
  PREVIEW_UPDATE = 'update-preview',
  PREVIEW_THEME_UPDATE = 'update-preview-theme',
  REQUEST_CURRENT_THEME = 'request-current-theme',
  THEME_CHANGED = 'theme-changed'
}

/**
 * Indicates the purpose of an image for accessibility reasons.
 *
 * @see {@link https://www.w3.org/WAI/tutorials/images/}
 */
export enum IMAGE_PURPOSE {
  /**
   * Informative images convey a simple concept or information that can be expressed in a short phrase or sentence.
   * The text alternative should convey the meaning or content that is displayed visually,
   * which typically isn’t a literal description of the image.
   */
  INFORMATIVE = 'informative',
  /**
   * Decorative images don’t add information to the content of a page.
   * For example, the information provided by the image might already be given using adjacent text,
   * or the image might be included to make the website more visually attractive.
   */
  DECORATIVE = 'decorative'
}
